import { Component, OnDestroy, OnInit } from '@angular/core';
import { GlobalGuideService } from '../../views/global-guide/global-guide.service';
import { Subscription, Subject, timer, merge, Observable, NEVER } from 'rxjs';
import {
  startWith,
  switchMap,
  mapTo,
  scan,
  withLatestFrom,
  shareReplay,
  tap,
  takeUntil,
  map,
  catchError,
} from 'rxjs/operators';
import { ReportVersionStatus } from '@xbs/xbs-enums';
import { AppService } from '../../services';

interface GlobalGuideReportState {
  reportId: string | null;
  reportVersionId: string | null;
  canDownload: boolean;
  canGenerate: boolean;
  isGenerating: boolean;
  isDownloading: boolean;
  isPolling: boolean;
}
@Component({
  selector: 'global-guide',
  templateUrl: './global-guide.component.html',
  styleUrls: ['./global-guide.component.scss'],
})
export class GlobalGuideComponent implements OnInit, OnDestroy {
  selectedRow = null;

  onContainerChangedSubscription: Subscription;

  globalGuideReportState: Observable<Partial<GlobalGuideReportState>>;

  private downloadReportClick = new Subject<void>();

  private generateReportClick = new Subject<void>();

  private unsubscribe = new Subject<void>();

  private programmaticEvent = new Subject<Partial<GlobalGuideReportState>>();

  constructor(
    public service: GlobalGuideService,
    public appService: AppService
  ) {}

  ngOnInit() {
    const pollInterval = 5000;

    const initialState = {
      isPolling: true,
      canDownload: false,
      isDownloading: false,
      canGenerate: false,
      isGenerating: false,
      reportVersionId: null,
      reportId: null,
    };

    this.globalGuideReportState = merge(
      this.generateReportClick.pipe(
        mapTo({
          isGenerating: true,
          isPolling: false,
          canGenerate: false,
        })
      ),
      this.downloadReportClick.pipe(
        mapTo({
          isDownloading: true,
          isPolling: false,
          canDownload: false,
        })
      ),
      this.programmaticEvent.asObservable()
    ).pipe(
      startWith(initialState),
      scan((state, partialState) => ({ ...state, ...partialState })),
      shareReplay(1)
    );

    const containerChange = this.appService.onContainerChanged
      .asObservable()
      .pipe(tap(() => this.programmaticEvent.next(initialState)));

    const generateReport = this.generateReportClick.pipe(
      withLatestFrom(this.globalGuideReportState),
      switchMap(([, state]) =>
        this.service.generateReportVersion(state.reportId)
      ),
      tap(
        (reportVersionId) =>
          this.programmaticEvent.next({ reportVersionId, isPolling: true }),
        () =>
          this.programmaticEvent.next({
            isGenerating: false,
            isPolling: true,
            canGenerate: true,
          })
      ),
      catchError(() => NEVER)
    );

    const downloadReport = this.downloadReportClick.pipe(
      withLatestFrom(this.globalGuideReportState),
      switchMap(([, state]) =>
        this.service.getReportVersionSignedUrl(
          state.reportId,
          state.reportVersionId
        )
      ),
      tap(
        (signedUrl) => {
          window.open(signedUrl, '_self');
          this.programmaticEvent.next({
            isDownloading: false,
            isPolling: true,
            canDownload: true,
          });
        },
        () =>
          this.programmaticEvent.next({
            isDownloading: false,
            isPolling: true,
            canDownload: true,
          })
      ),
      catchError(() => NEVER)
    );

    const getReportStatus = merge(containerChange, timer(0, pollInterval)).pipe(
      withLatestFrom(this.globalGuideReportState),
      switchMap(([, state]) =>
        state.isPolling ?
          this.service.getReportMeta().pipe(
            map(
              (reportMeta) =>
                ({
                  [ReportVersionStatus.ByName.NoReports.Id]: {
                    reportId: reportMeta.reportId,
                    canGenerate: true,
                    canDownload: false,
                  },
                  [ReportVersionStatus.ByName.Pending.Id]: {
                    reportId: reportMeta.reportId,
                    canGenerate: false,
                    canDownload: false,
                    isGenerating: true,
                  },
                  [ReportVersionStatus.ByName.Generated.Id]: {
                    reportId: reportMeta.reportId,
                    reportVersionId:
                        state.reportVersionId ||
                        (reportMeta.hasVersions &&
                          reportMeta.versions[0].versionId),
                    canGenerate: true,
                    canDownload: true,
                    isGenerating: false,
                  },
                  [ReportVersionStatus.ByName.Failed.Id]: {
                    reportId: reportMeta.reportId,
                    reportVersionId:
                        state.reportVersionId ||
                        (reportMeta.hasVersions &&
                          reportMeta.versions[0].versionId),
                    canGenerate: true,
                    canDownload: reportMeta.hasVersions,
                    isGenerating: false,
                  },
                }[reportMeta.reportStatus])
            ),
            catchError(() => NEVER)
          ) :
          NEVER
      ),
      tap(
        (state) => this.programmaticEvent.next(state),
        () => {
          this.programmaticEvent.next(initialState);
        }
      )
    );

    merge(containerChange, generateReport, downloadReport, getReportStatus)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  /**
   * Callback to set the `selectedCountry` on the service.
   */
  countrySelected({ row }) {
    const { countryName } = row;
    this.service.selectedCountry = countryName;
  }

  getSelectedRow() {
    return this.service.tableData.rows.findIndex((row) => {
      return row.countryName === this.service.selectedCountry;
    });
  }

  generateReport() {
    this.generateReportClick.next();
  }

  downloadReport() {
    this.downloadReportClick.next();
  }
}
